import { PlacementType } from '../types';

interface PositionData {
  top: number;
  left: number;
}

interface ArrowPosition {
  top: number;
  left: number;
}

interface UpdatePositionResult {
  position: PositionData;
  arrowPosition: ArrowPosition;
}

export const getPosition = (
  anchorElement: HTMLElement | null | undefined,
  tooltipElement: HTMLElement | null | undefined,
  placement: PlacementType,
  container: HTMLElement = document.body
): UpdatePositionResult => {
  if (!anchorElement || !tooltipElement) {
    return {
      position: { top: -1000, left: -1000 },
      arrowPosition: { top: -1000, left: -1000 }
    };
  }
  
  // Get all relevant element boundaries
  const anchorRect = anchorElement.getBoundingClientRect();
  const tooltipRect = tooltipElement.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  
  // Calculate relative positions to the container
  const anchorTop = anchorRect.top - containerRect.top;
  const anchorLeft = anchorRect.left - containerRect.left;
  const anchorWidth = anchorRect.width;
  const anchorHeight = anchorRect.height;
  
  let top = 0;
  let left = 0;
  let arrowTop = 0;
  let arrowLeft = 0;
  
  // Spacing between elements
  const margin = 0;
  
  // Position based on placement
  switch (placement) {
    case 'top':
      // Position tooltip above the anchor
      top = anchorTop - tooltipRect.height - margin;
      left = anchorLeft + (anchorWidth / 2) - (tooltipRect.width / 2);
      // Center arrow horizontally
      arrowLeft = tooltipRect.width / 2;
      arrowTop = tooltipRect.height;
      break;
    case 'top-start':
      top = anchorTop - tooltipRect.height - margin;
      left = anchorLeft;
      // Position arrow at start but with offset
      arrowLeft = Math.min(anchorWidth / 2, tooltipRect.width / 4);
      arrowTop = tooltipRect.height;
      break;
    case 'top-end':
      top = anchorTop - tooltipRect.height - margin;
      left = anchorLeft + anchorWidth - tooltipRect.width;
      // Position arrow toward the anchor element
      arrowLeft = tooltipRect.width - Math.min(anchorWidth / 2, tooltipRect.width / 4);
      arrowTop = tooltipRect.height;
      break;
    case 'right':
      top = anchorTop + (anchorHeight / 2) - (tooltipRect.height / 2);
      left = anchorLeft + anchorWidth + margin;
      // Center arrow vertically
      arrowLeft = 0;
      arrowTop = tooltipRect.height / 2;
      break;
    case 'right-start':
      top = anchorTop;
      left = anchorLeft + anchorWidth + margin;
      // Position arrow at start
      arrowLeft = 0;
      arrowTop = Math.min(anchorHeight / 2, tooltipRect.height / 4);
      break;
    case 'right-end':
      top = anchorTop + anchorHeight - tooltipRect.height;
      left = anchorLeft + anchorWidth + margin;
      // Position arrow at end
      arrowLeft = 0;
      arrowTop = tooltipRect.height - Math.min(anchorHeight / 2, tooltipRect.height / 4);
      break;
    case 'bottom':
      top = anchorTop + anchorHeight + margin;
      left = anchorLeft + (anchorWidth / 2) - (tooltipRect.width / 2);
      // Center arrow horizontally
      arrowLeft = tooltipRect.width / 2;
      arrowTop = 0;
      break;
    case 'bottom-start':
      top = anchorTop + anchorHeight + margin;
      left = anchorLeft;
      // Position arrow at start but with offset
      arrowLeft = Math.min(anchorWidth / 2, tooltipRect.width / 4);
      arrowTop = 0;
      break;
    case 'bottom-end':
      top = anchorTop + anchorHeight + margin;
      left = anchorLeft + anchorWidth - tooltipRect.width;
      // Position arrow at end but with offset
      arrowLeft = tooltipRect.width - Math.min(anchorWidth / 2, tooltipRect.width / 4);
      arrowTop = 0;
      break;
    case 'left':
      top = anchorTop + (anchorHeight / 2) - (tooltipRect.height / 2);
      left = anchorLeft - tooltipRect.width - margin;
      // Center arrow vertically
      arrowLeft = tooltipRect.width;
      arrowTop = tooltipRect.height / 2;
      break;
    case 'left-start':
      top = anchorTop;
      left = anchorLeft - tooltipRect.width - margin;
      // Position arrow at start
      arrowLeft = tooltipRect.width;
      arrowTop = Math.min(anchorHeight / 2, tooltipRect.height / 4);
      break;
    case 'left-end':
      top = anchorTop + anchorHeight - tooltipRect.height;
      left = anchorLeft - tooltipRect.width - margin;
      // Position arrow at end
      arrowLeft = tooltipRect.width;
      arrowTop = tooltipRect.height - Math.min(anchorHeight / 2, tooltipRect.height / 4);
      break;
  }
  
  // Ensure tooltip stays within container boundaries
  top = Math.max(0, Math.min(top, containerRect.height - tooltipRect.height));
  left = Math.max(0, Math.min(left, containerRect.width - tooltipRect.width));
  
  return {
    position: { top, left },
    arrowPosition: { top: arrowTop, left: arrowLeft }
  };
};
